/* The Loom sign-in page — /login
 *
 * iter55f83 (2026-02): thinned from 663 → ~140 lines by extracting
 * three concerns to `pages/login/`:
 *   • LoginSignInCard     — the sign-in card
 *   • LoginSignInCard    — the Google button + admin gateway
 *   • LoginForumStream   — "The Margin · Forum" preview + composer
 *
 * The remaining shell keeps the page-level chrome (background, brand
 * strip, headline, ribbon, LeftRailNav, salon modal, footer) and the
 * one-shot hash-scroll for `/login#community` deep-links. Every
 * original testid is still emitted by the child components.
 */
import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth_context';
import { GRAIN_URL, useScriptoriumFonts, P, FONTS } from '@/components/landing/scriptorium/palette';

import WritersSalonModal from '@/components/landing/scriptorium/WritersSalonModal';
import PublicNavBar from '@/components/landing/PublicNavBar';
import LoginSignInCard from './login/LoginSignInCard';
import LoginForumStream from './login/LoginForumStream';
import PoweredByEmergent from '@/components/landing/PoweredByEmergent';
import { useSeoMeta } from '@/lib/useSeoMeta';

// 2026-10 — SEO audit fix: backend already had a DEFAULT_SEO_LOGIN
// (noindex) default, but nothing called useSeoMeta here, so the live
// DOM kept whatever title/robots the previous page left behind.
const SEO_FALLBACK = {
  title:       'Sign In — Crown & Rose',
  description: 'Sign in to The Loom, your Crown & Rose author workspace.',
  keywords:    '',
};

export default function LoginPage() {
  useScriptoriumFonts();
  useSeoMeta('/login', SEO_FALLBACK);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  // iter55g66 — Surface Google-auth exchange failures. AuthCallback now
  // redirects to `/login?auth_error=<msg>` instead of silently
  // recycling to /app.
  const [searchParams, setSearchParams] = useSearchParams();
  const authError = searchParams.get('auth_error') || '';
  const dismissAuthError = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('auth_error');
    setSearchParams(next, { replace: true });
  };

  const [salonOpen, setSalonOpen] = useState(false);
  const [salonMode, setSalonMode] = useState('signin');
  const openSalon = (mode = 'signin') => {
    setSalonMode(mode);
    setSalonOpen(true);
  };
  const rootScrollRef = useRef(null);

  /* Deep-link support: /login#community scrolls the forum stream into
   * view once, then strips the hash from the URL so subsequent layout
   * shifts (activity feed refetch, composer submit re-render) don't
   * re-anchor the browser to the bottom of the page and lock the user
   * out of scrolling back up.
   *
   * We use a direct `scrollTo` on our own root container (which owns
   * the scroll via `overflow-y-auto`) rather than
   * `Element.scrollIntoView` because scrollIntoView semantics vary
   * across browsers when the target has an `overflow-y-auto`
   * ancestor. */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash !== '#community') return;
    const t = window.setTimeout(() => {
      const root = rootScrollRef.current;
      const target = document.getElementById('login-forum-stream');
      if (root && target) {
        const y = target.offsetTop - 12;
        root.scrollTo({ top: y, behavior: 'smooth' });
      }
      try { window.history.replaceState({}, '', '/login'); } catch (_e) { /* noop */ }
    }, 200);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div
      ref={rootScrollRef}
      /* Root is `h-screen` (not `min-h-screen`) so the child's own
       * `overflow-y-auto` correctly scrolls when content is taller
       * than the viewport. Under the .App wrapper (which is
       * `h-screen overflow-hidden`) a min-h-screen child grows past
       * the clip and becomes un-scrollable on short viewports; a
       * fixed h-screen child owns the scroll cleanly. */
      className="relative h-screen w-screen overflow-y-auto"
      style={{ backgroundColor: P.paper, color: P.ink, fontFamily: FONTS.body }}
      data-testid="login-page-root"
    >
      {/* Site-wide nav — matches landing page header */}
      <PublicNavBar
        onSignIn={() => openSalon('signin')}
        onSignUp={() => navigate('/')}
      />

      {/* Grain overlay */}
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-30"
        style={{ backgroundImage: GRAIN_URL }}
      />


      {/* Watermark emblem preserved from the previous version. */}
      <img
        src="/brand/crownandrose-logo.png"
        alt=""
        aria-hidden="true"
        data-testid="login-logo-watermark"
        className="pointer-events-none absolute right-6 bottom-6 z-[1] hidden h-40 w-40 select-none object-contain opacity-[0.08] mix-blend-multiply lg:block"
        draggable={false}
      />

      <div className="relative z-10 mx-auto max-w-2xl px-6 py-10">
        {/* Notepad + sign-in — card constrained to ~⅓ the container width to
            match the compact look of the landing page "Already writing with us?"
            strip. The outer max-w-2xl container is preserved unchanged. */}
        <div className="mt-8 mx-auto max-w-xs" data-testid="login-workspace-grid">
          <LoginSignInCard
            onGoogleSignIn={login}
            onEmailMode={openSalon}
            authError={authError}
            onDismissAuthError={dismissAuthError}
          />
        </div>

        <LoginForumStream user={user} />

        <div
          className="mt-8 text-[11px]"
          style={{ color: P.inkSoft, fontFamily: FONTS.body }}
        >
          © 2026 Crown &amp; Rose LLC · All rights reserved ·{' '}
          <a className="hover:underline" style={{ color: P.oxblood }} href="mailto:info@crownnrose.com">
            info@crownnrose.com
          </a>
        </div>
        <PoweredByEmergent testId="login-powered-by-emergent" className="text-[color:var(--gold)]" />
      </div>

      <WritersSalonModal open={salonOpen} onClose={() => setSalonOpen(false)} initialMode={salonMode} />
    </div>
  );
}
